import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Globe, Loader2, FileText, Users, MapPin, Target, Filter, Search, CheckCircle2, X, ChevronRight, Sparkles, Eye, Rocket
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import { adminApi } from "@/lib/api";
import { EMAIL_TEMPLATES } from "./leadgen/emailTemplates";
import { ResultsPanel } from "./leadgen/ResultsPanel";
import { EmailModal } from "./leadgen/EmailModal";
import { MailboxGuide } from "./leadgen/MailboxGuide";
import { FollowupWidget } from "./leadgen/FollowupWidget";

const COUNTRIES = [
  { code: "IT", label: "🇮🇹 Italia" },
  { code: "CH", label: "🇨🇭 Svizzera" },
];
const PROFESSIONS_SUGG = ["Ristorante", "Pizzeria", "Parrucchiere", "Estetica", "Palestra", "Clinica", "Immobiliare", "Idraulico", "Elettricista", "Fabbro", "Catering", "Bar", "Sala da ballo"];

export const LeadGenPanel = ({ target }) => {
  const [step, setStep] = useState(1);
  const [analyzing, setAnalyzing] = useState(false);
  const [siteUrl, setSiteUrl] = useState("");
  const [pdfData, setPdfData] = useState(null);

  // ICP state
  const [sectors, setSectors] = useState([]);
  const [atecoQuery, setAtecoQuery] = useState("");
  const [atecoResults, setAtecoResults] = useState([]);
  const [gender, setGender] = useState("all");
  const [ageRange, setAgeRange] = useState([25, 60]);
  const [professions, setProfessions] = useState([]);
  const [customProf, setCustomProf] = useState("");
  const [geoType, setGeoType] = useState("city");
  const [geoCities, setGeoCities] = useState([]);
  const [cityInput, setCityInput] = useState("");
  const [geoRadius, setGeoRadius] = useState(50);
  const [geoCenter, setGeoCenter] = useState(null);
  const [countries, setCountries] = useState(["IT", "CH"]);
  const [estimate, setEstimate] = useState(null);

  // Results
  const [searching, setSearching] = useState(false);
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState({});

  // Email modal
  const [emailModal, setEmailModal] = useState(null);

  useEffect(() => { adminApi.leadSources().then(setSources).catch(() => {}); }, []);

  const loadSavedLeads = async () => {
    try {
      const d = await adminApi.listLeads({ limit: 500 });
      setLeads(d.leads);
      setStep(4);
      toast.success(`${d.leads.length} lead salvati caricati`);
    } catch {
      toast.error("Errore caricamento lead");
    }
  };

  useEffect(() => { if (target) loadSavedLeads(); }, [target?.at]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateLead = (updated) => setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));

  const openEmail = (targets, templateId = EMAIL_TEMPLATES[0].id) => {
    const t = EMAIL_TEMPLATES.find((x) => x.id === templateId);
    setEmailModal({ leads: targets, templateId: t.id, subject: t.subject, message: t.message });
  };

  // Listen for step-change events (from inner components)
  useEffect(() => {
    const handler = (e) => setStep(e.detail);
    window.addEventListener("leadgen-step", handler);
    return () => window.removeEventListener("leadgen-step", handler);
  }, []);

  // ATECO autocomplete
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (atecoQuery.length < 2) { setAtecoResults([]); return; }
      try {
        const d = await adminApi.atecoSearch(atecoQuery);
        setAtecoResults(d.results);
      } catch {}
    }, 300);
    return () => clearTimeout(timer);
  }, [atecoQuery]);

  const addSector = (s) => {
    if (!sectors.find((x) => x.code === s.code)) setSectors([...sectors, { code: s.code, desc: s.desc }]);
    setAtecoQuery("");
    setAtecoResults([]);
  };

  const addProfession = (p) => {
    const v = p.trim();
    if (v && !professions.includes(v)) setProfessions([...professions, v]);
    setCustomProf("");
  };

  const addCity = (c) => {
    const v = c.trim();
    if (v && !geoCities.includes(v)) setGeoCities([...geoCities, v]);
    setCityInput("");
  };

  const useGeoLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocalizzazione non supportata");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCenter({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        toast.success(`Posizione: ${pos.coords.latitude.toFixed(3)}, ${pos.coords.longitude.toFixed(3)}`);
      },
      () => toast.error("Impossibile ottenere posizione")
    );
  };

  const onAnalyzeSite = async () => {
    if (!siteUrl.trim()) return toast.error("Inserisci un URL");
    setAnalyzing(true);
    try {
      const d = await adminApi.analyzeSite(siteUrl);
      setPdfData(d);
      toast.success("PDF generato · prosegui con l'ICP");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore analisi");
    } finally {
      setAnalyzing(false);
    }
  };

  const onEstimate = async () => {
    try {
      const d = await adminApi.estimate(buildICP());
      setEstimate(d);
    } catch (e) {
      toast.error("Errore stima");
    }
  };

  const loadFromCampaign = (c) => {
    const p = c.params || {}, t = c.targeting || {};
    setProfessions((p.skill || "").split(",").map((x) => x.trim()).filter(Boolean));
    setGeoType("city");
    setGeoCities([p.area].filter(Boolean));
    setGender(t.gender || "all");
    setAgeRange([t.age_min || 25, t.age_max || 60]);
    setCountries(CAMPAIGN_CH_RE.test(p.area || "") || c.currency === "chf" ? ["CH"] : ["IT", "CH"]);
    setEstimate(null);
    toast.success(`ICP caricato dalla campagna di ${c.client_name || c.client_email}`);
  };

  const buildICP = () => ({
    sectors,
    gender,
    age_min: ageRange[0],
    age_max: ageRange[1],
    professions,
    geo_type: geoType,
    geo_cities: geoType === "city" ? geoCities : [],
    geo_center: geoType === "radius" ? geoCenter : null,
    geo_radius_km: geoType === "radius" ? geoRadius : null,
    countries,
  });

  const onSearch = async () => {
    setSearching(true);
    try {
      const payload = { ...buildICP(), limit: 50, pdf_id: pdfData?.id };
      const { id } = await adminApi.startScrapeJob(payload);
      let d = null;
      for (let i = 0; i < 120 && !d; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const j = await adminApi.getScrapeJob(id);
        if (j.status === "error") throw new Error(j.error);
        if (j.status === "done") d = j;
      }
      if (!d) throw new Error("Tempo scaduto: riprova");
      setLeads(d.leads);
      setStep(4);
      toast.success(`${d.leads.length} lead reali trovati`);
      (d.report?.warnings || []).forEach((w) => toast.warning(w));
    } catch (e) {
      toast.error(e?.message || "Errore ricerca");
    } finally {
      setSearching(false);
    }
  };


  return (
    <div className="space-y-6">
      <MailboxGuide />
      <FollowupWidget onFollowup={(targets) => openEmail(targets, "followup")} refreshKey={emailModal} />
      {/* Steps indicator */}
      <StepsBar step={step} onStep={setStep} />

      {step === 1 && (
        <div className="mb-4"><CampaignPicker onLoad={(c) => { loadFromCampaign(c); setStep(2); }} /></div>
      )}
      {step === 1 && (
        <SiteAnalyzer siteUrl={siteUrl} setSiteUrl={setSiteUrl} analyzing={analyzing}
          onAnalyze={onAnalyzeSite} pdfData={pdfData} onLoadSaved={loadSavedLeads} />
      )}

      {step === 2 && (
        <ICPBuilder
          sectors={sectors} setSectors={setSectors} atecoQuery={atecoQuery} setAtecoQuery={setAtecoQuery}
          atecoResults={atecoResults} addSector={addSector}
          gender={gender} setGender={setGender} ageRange={ageRange} setAgeRange={setAgeRange}
          professions={professions} setProfessions={setProfessions} customProf={customProf} setCustomProf={setCustomProf}
          addProfession={addProfession}
          geoType={geoType} setGeoType={setGeoType} geoCities={geoCities} setGeoCities={setGeoCities}
          cityInput={cityInput} setCityInput={setCityInput} addCity={addCity} geoRadius={geoRadius} setGeoRadius={setGeoRadius}
          geoCenter={geoCenter} useGeoLocation={useGeoLocation}
          countries={countries} setCountries={setCountries}
          estimate={estimate} onEstimate={onEstimate} onNext={() => setStep(3)} onLoadCampaign={loadFromCampaign}
        />
      )}

      {step === 3 && (
        <SearchLaunch icp={buildICP()} sources={sources} estimate={estimate} searching={searching} onSearch={onSearch} onBack={() => setStep(2)} />
      )}

      {step === 4 && (
        <ResultsPanel leads={leads} onEmail={openEmail} onUpdated={updateLead} target={target} />
      )}

      {emailModal && (
        <EmailModal data={emailModal} setData={setEmailModal} onContacted={updateLead} pdfData={pdfData} />
      )}
    </div>
  );
};


const StepsBar = ({ step, onStep }) => {
  const steps = [
    { n: 1, label: "Analizza sito" },
    { n: 2, label: "ICP targeting" },
    { n: 3, label: "Lancia ricerca" },
    { n: 4, label: "Risultati" },
  ];
  return (
    <div className="border border-[#d5dbde] bg-white flex">
      {steps.map((s, i) => (
        <button key={s.n} onClick={() => s.n < step && onStep(s.n)}
          disabled={s.n > step}
          className={`flex-1 flex items-center gap-3 px-5 py-4 border-r border-[#d5dbde] last:border-r-0 transition-colors ${
            step === s.n ? "bg-[#f4f6f7]" : "hover:bg-[#f4f6f7]/50"
          } ${s.n > step ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
          <span className={`w-7 h-7 flex items-center justify-center font-mono text-xs ${
            step >= s.n ? "aurora-gradient text-white" : "border border-[#d5dbde] text-[#6b7280]"
          }`}>
            {step > s.n ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
          </span>
          <span className={`text-sm font-medium ${step === s.n ? "text-[#2B3036]" : "text-[#6b7280]"}`}>{s.label}</span>
        </button>
      ))}
    </div>
  );
};


const SiteAnalyzer = ({ siteUrl, setSiteUrl, analyzing, onAnalyze, pdfData, onLoadSaved }) => (
  <div className="border border-[#d5dbde] bg-white p-8">
    <div className="mb-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
      <div>
      <h3 className="font-display text-2xl mb-2">Step 1 · Analizza il sito del cliente</h3>
      <p className="text-sm text-[#5b6168] max-w-2xl">Inseriamo l'URL del sito del cliente. L'AI (Claude Sonnet 4.6) analizza struttura, servizi, tone of voice e genera una presentazione PDF Aurora-brandizzata pronta per l'invio ai lead.</p>
      </div>
      <Button data-testid="leadgen-load-saved-btn" onClick={onLoadSaved} variant="outline"
        className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7] rounded-sm whitespace-nowrap">
        <Users className="w-4 h-4 mr-2" /> Apri lead salvati (CRM)
      </Button>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 max-w-3xl">
      <div className="relative flex-1">
        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
        <Input data-testid="leadgen-site-input" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !analyzing && onAnalyze()}
          placeholder="https://www.cliente.com" disabled={analyzing}
          className="pl-10 h-12 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] placeholder:text-[#52525b] rounded-sm font-mono text-sm" />
      </div>
      <Button data-testid="leadgen-analyze-btn" onClick={onAnalyze} disabled={analyzing}
        className="aurora-gradient hover:opacity-90 text-white rounded-sm h-12 px-6">
        {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analisi + PDF...</> : <>Analizza & Genera PDF <ChevronRight className="w-4 h-4 ml-1" /></>}
      </Button>
    </div>

    {analyzing && (
      <div className="mt-6 font-mono text-xs text-[#6b7280] space-y-1">
        <div>▸ Fetch del sito...</div>
        <div>▸ Parsing contenuto...</div>
        <div className="aurora-text font-semibold">▸ Claude genera contenuti narrativi...</div>
        <div>▸ Rendering PDF Aurora-brandizzato...</div>
      </div>
    )}

    {pdfData && (
      <>
      <div className="mt-6 border border-[#10b981] bg-[#042f1f] p-6 flex flex-col lg:flex-row items-start lg:items-center gap-6">
        <div className="w-14 h-14 aurora-gradient flex items-center justify-center flex-shrink-0">
          <FileText className="w-7 h-7 text-[#2B3036]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#10b981] font-mono mb-1">● PDF generato</div>
          <h4 className="font-display text-xl mb-1">{pdfData.narrative?.brand}</h4>
          <p className="text-sm text-[#5b6168]">{pdfData.narrative?.tagline}</p>
          <div className="text-xs text-[#6b7280] font-mono mt-2">
            Settore: <span className="text-[#2B3036]">{pdfData.narrative?.sector}</span> · {pdfData.size_kb} KB
          </div>
        </div>
        <a data-testid="leadgen-pdf-preview" href={pdfData.preview_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-[#f4f4f5]">
          <Eye className="w-4 h-4" /> Anteprima PDF
        </a>
      </div>
      <div className="mt-4 flex justify-end">
        <Button data-testid="leadgen-goto-icp" onClick={() => window.scrollTo({top:0,behavior:'smooth'}) || window.dispatchEvent(new CustomEvent('leadgen-step', {detail:2}))}
          className="aurora-gradient hover:opacity-90 text-white rounded-sm h-11 px-6">
          Prosegui con ICP targeting <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      </>
    )}
  </div>
);


const CAMPAIGN_CH_RE = /sviz|swit|ticino|lugan|zur|zür|gine|genev|bern|basil|lucern|locarno|bellinzona|paradiso|massagno|mendrisio|chiasso/i;

const CampaignPicker = ({ onLoad }) => {
  const [camps, setCamps] = useState([]);
  useEffect(() => { adminApi.managedList().then((d) => setCamps(d.campaigns)).catch(() => {}); }, []);
  return (
    <div data-testid="icp-campaign-picker" className="border border-[#E0262C]/40 bg-[#fdecec] p-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-[#2B3036]">Usa i dati di un ordine cliente</div>
        <div className="text-[11px] text-[#5b6168]">Carica skill, zona e target già compilati dal cliente. Puoi poi modificarli a mano.</div>
      </div>
      <select data-testid="icp-campaign-select" defaultValue="" onChange={(e) => { const c = camps.find((x) => x.id === e.target.value); if (c) onLoad(c); }}
        className="h-10 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] text-sm px-3 md:w-[420px]">
        <option value="" disabled>{camps.length ? "Seleziona campagna cliente…" : "Nessuna campagna cliente"}</option>
        {camps.map((c) => (
          <option key={c.id} value={c.id}>{`${c.client_name || c.client_email} · ${c.params?.area || ""} · ${(c.params?.skill || "").slice(0, 50)} · ${c.status_label || ""}`}</option>
        ))}
      </select>
    </div>
  );
};

const ICPBuilder = (p) => (
  <div className="border border-[#d5dbde] bg-white p-8 space-y-8">
    <div>
      <h3 className="font-display text-2xl mb-2">Step 2 · Costruisci il tuo ICP</h3>
      <p className="text-sm text-[#5b6168]">Definisci il profilo cliente ideale. Più preciso è il targeting, più qualificati saranno i lead.</p>
    </div>
    <CampaignPicker onLoad={p.onLoadCampaign} />

    {/* Sectors */}
    <div>
      <Label icon={Filter}>Settore merceologico (ATECO/NOGA)</Label>
      <div className="flex flex-wrap gap-2 mb-3">
        {p.sectors.map((s) => (
          <Chip key={s.code} onRemove={() => p.setSectors(p.sectors.filter((x) => x.code !== s.code))}>
            <span className="font-mono text-xs text-[#E0262C]">{s.code}</span> {s.desc}
          </Chip>
        ))}
      </div>
      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7280]" />
        <Input data-testid="leadgen-ateco-input" value={p.atecoQuery} onChange={(e) => p.setAtecoQuery(e.target.value)}
          placeholder="Cerca settore (es. ristorante, parrucchiere)"
          className="pl-10 h-11 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm" />
        {p.atecoResults.length > 0 && (
          <div className="absolute z-20 top-12 left-0 right-0 bg-[#f4f6f7] border border-[#d5dbde] max-h-60 overflow-y-auto">
            {p.atecoResults.map((r) => (
              <button key={r.code + r.country} onClick={() => p.addSector(r)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-[#e4e7e9] flex items-center gap-2 border-b border-[#d5dbde] last:border-0">
                <span className="font-mono text-xs text-[#E0262C] w-20">{r.code}</span>
                <span className="flex-1">{r.desc}</span>
                <span className="text-[10px] font-mono text-[#6b7280]">{r.country}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>

    {/* Target demografico */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <Label icon={Users}>Genere</Label>
        <RadioGroup value={p.gender} onValueChange={p.setGender} className="grid grid-cols-3 gap-2">
          {[["all","Tutti"],["male","Uomo"],["female","Donna"]].map(([v,l]) => (
            <label key={v} className={`cursor-pointer border p-3 flex items-center justify-center text-sm font-medium transition-colors ${
              p.gender === v ? "border-[#E0262C] bg-[#E0262C]/10 text-white" : "border-[#d5dbde] text-[#5b6168] hover:border-[#52525b]"
            }`}>
              <RadioGroupItem value={v} className="sr-only" />{l}
            </label>
          ))}
        </RadioGroup>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label>Fascia età</Label>
          <span className="font-mono text-sm text-[#2B3036]">{p.ageRange[0]} — {p.ageRange[1]}</span>
        </div>
        <div className="px-1 py-3"><Slider min={18} max={80} value={p.ageRange} onValueChange={p.setAgeRange} /></div>
      </div>
    </div>

    {/* Professions */}
    <div>
      <Label icon={Target}>Professione</Label>
      <div className="flex flex-wrap gap-2 mb-3">
        {p.professions.map((prof, i) => (
          <Chip key={i} onRemove={() => p.setProfessions(p.professions.filter((_, idx) => idx !== i))}>{prof}</Chip>
        ))}
      </div>
      <Input value={p.customProf} onChange={(e) => p.setCustomProf(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); p.addProfession(p.customProf); } }}
        placeholder="Aggiungi professione custom + Invio"
        className="h-11 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm" />
      <div className="flex flex-wrap gap-1.5 mt-2">
        {PROFESSIONS_SUGG.filter((s) => !p.professions.includes(s)).map((s) => (
          <button key={s} onClick={() => p.addProfession(s)}
            className="text-xs text-[#6b7280] hover:text-[#E0262C] font-mono border border-dashed border-[#d5dbde] px-2 py-1">
            + {s}
          </button>
        ))}
      </div>
    </div>

    {/* Geo */}
    <div>
      <Label icon={MapPin}>Zona geografica</Label>
      <div className="flex gap-2 mb-4">
        {[["city","Città"],["radius","Raggio KM"],["region","Regione"]].map(([v,l]) => (
          <button key={v} onClick={() => p.setGeoType(v)}
            className={`px-4 py-2 text-sm font-medium border transition-colors ${
              p.geoType === v ? "border-[#E0262C] bg-[#E0262C]/10 text-white" : "border-[#d5dbde] text-[#5b6168]"
            }`}>{l}</button>
        ))}
      </div>
      {p.geoType === "city" && (
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            {p.geoCities.map((c, i) => (
              <Chip key={i} onRemove={() => p.setGeoCities(p.geoCities.filter((_, idx) => idx !== i))}>{c}</Chip>
            ))}
          </div>
          <Input value={p.cityInput} onChange={(e) => p.setCityInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); p.addCity(p.cityInput); } }}
            placeholder="Milano, Zürich, Roma... + Invio"
            className="h-11 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm" />
        </div>
      )}
      {p.geoType === "radius" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#5b6168]">Raggio</span>
            <span className="font-mono text-sm text-[#2B3036]">{p.geoRadius} km</span>
          </div>
          <Slider min={5} max={500} value={[p.geoRadius]} onValueChange={(v) => p.setGeoRadius(v[0])} />
          <div className="flex items-center gap-3">
            <Button onClick={p.useGeoLocation} variant="outline" size="sm"
              className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7]">
              <MapPin className="w-3.5 h-3.5 mr-2" /> Usa la mia posizione
            </Button>
            {p.geoCenter && <span className="text-xs font-mono text-[#10b981]">● {p.geoCenter.lat.toFixed(3)}, {p.geoCenter.lon.toFixed(3)}</span>}
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <span className="text-xs text-[#6b7280] font-mono">Paesi:</span>
        {COUNTRIES.map((c) => (
          <button key={c.code} onClick={() => p.setCountries(p.countries.includes(c.code)
            ? p.countries.filter((x) => x !== c.code)
            : [...p.countries, c.code])}
            className={`px-3 py-1 text-xs border font-medium ${
              p.countries.includes(c.code) ? "border-[#E0262C] bg-[#E0262C]/10 text-white" : "border-[#d5dbde] text-[#6b7280]"
            }`}>{c.label}</button>
        ))}
      </div>
    </div>

    {/* Estimate CTA */}
    <div className="border-t border-[#d5dbde] pt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <Button onClick={p.onEstimate} variant="outline"
        className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7]">
        <Sparkles className="w-4 h-4 mr-2" /> Stima contatti potenziali
      </Button>
      {p.estimate && (
        <div className="font-mono text-sm text-[#5b6168]">
          <span className="aurora-text font-display text-2xl">~{p.estimate.estimated_total.toLocaleString()}</span> contatti stimati
        </div>
      )}
      <div className="ml-auto">
        <Button data-testid="leadgen-next-btn" onClick={p.onNext}
          className="aurora-gradient hover:opacity-90 text-white rounded-sm h-11 px-6">
          Avanti <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>

    {p.estimate && p.estimate.cities_breakdown.length > 0 && (
      <div className="border border-[#d5dbde] p-4 bg-[#f4f6f7]">
        <div className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280] mb-3">Breakdown per città</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {p.estimate.cities_breakdown.slice(0, 8).map((c) => (
            <div key={c.city} className="flex items-center justify-between text-sm">
              <span className="text-[#5b6168]">{c.city}</span>
              <span className="font-mono text-xs text-[#2B3036]">{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);


const SOURCE_LABELS = [
  ["openstreetmap", "OpenStreetMap + siti web", "aziende reali, email e social dai siti"],
  ["google_places_api_key", "Google Places", "aziende + recensioni reali"],
  ["perplexity_api_key", "Perplexity", "responsabili / decision maker"],
  ["apify_api_token", "Apify", "profili Instagram e TikTok"],
  ["hunter_api_key", "Hunter.io", "email trovate + verificate"],
  ["apollo_api_key", "Apollo.io", "dipendenti, fatturato, LinkedIn"],
];

const SourcesStatus = ({ sources }) => (
  <div data-testid="leadgen-sources-status" className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-6">
    {SOURCE_LABELS.map(([k, name, desc]) => (
      <div key={k} className="border border-[#d5dbde] p-3 flex items-center gap-3">
        <span className={`w-2 h-2 rounded-full ${sources[k] ? "bg-[#10b981]" : "bg-[#52525b]"}`} />
        <div className="min-w-0">
          <div className="text-sm text-[#2B3036]">{name} <span className={`text-[10px] font-mono uppercase ${sources[k] ? "text-[#10b981]" : "text-[#6b7280]"}`}>{sources[k] ? "attivo" : "non configurato"}</span></div>
          <div className="text-[11px] text-[#6b7280] truncate">{desc}</div>
        </div>
      </div>
    ))}
  </div>
);

const SearchLaunch = ({ icp, sources, estimate, searching, onSearch, onBack }) => (
  <div className="border border-[#d5dbde] bg-white p-8">
    <h3 className="font-display text-2xl mb-2">Step 3 · Pronti per il lancio</h3>
    <p className="text-sm text-[#5b6168] mb-6">Controlla i parametri e avvia la ricerca di lead.</p>
    <SourcesStatus sources={sources} />

    <div className="border border-[#d5dbde] p-5 mb-6 space-y-3 text-sm">
      <Row label="Paesi" value={icp.countries.join(" · ")} />
      <Row label="Settori" value={icp.sectors.map((s) => s.desc).join(", ") || "-"} />
      <Row label="Professioni" value={icp.professions.join(", ") || "-"} />
      <Row label="Età" value={`${icp.age_min} - ${icp.age_max} anni`} />
      <Row label="Zona" value={icp.geo_type === "city" ? icp.geo_cities.join(", ") || "tutte" : icp.geo_type === "radius" ? `${icp.geo_radius_km} km da ${icp.geo_center ? "posizione corrente" : "nessuna"}` : "regioni"} />
      {estimate && <Row label="Stima lead" value={`~${estimate.estimated_total.toLocaleString()}`} />}
    </div>

    <div className="flex items-center justify-between">
      <Button onClick={onBack} variant="outline"
        className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7]">
        Modifica ICP
      </Button>
      <Button data-testid="leadgen-start-btn" onClick={onSearch} disabled={searching}
        className="aurora-gradient hover:opacity-90 text-white rounded-sm h-12 px-8">
        {searching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scraping lead...</> : <><Rocket className="w-4 h-4 mr-2" /> Avvia Ricerca Lead</>}
      </Button>
    </div>
  </div>
);


const Label = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#6b7280] font-mono mb-3">
    {Icon && <Icon className="w-3.5 h-3.5" />}{children}
  </div>
);

const Chip = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 bg-[#f4f6f7] border border-[#d5dbde] px-2.5 py-1 text-sm text-[#2B3036]">
    {children}
    <button onClick={onRemove} className="text-[#6b7280] hover:text-[#ef4444]"><X className="w-3 h-3" /></button>
  </span>
);

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280] whitespace-nowrap">{label}</span>
    <span className="text-sm text-[#2B3036] text-right truncate max-w-[75%]">{value}</span>
  </div>
);
